import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { CUTSCENE_SCRIPT } from '../logic/cutscene';
import { getSpriteUrl } from '../logic/assetUtils';
import { MotionLines } from './Effects';

const Cutscene = ({ onComplete }) => {
  const [currentScene, setCurrentScene] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const containerRef = useRef();
  const foregroundRef = useRef();
  const backgroundRef = useRef();
  const bgImageRef = useRef();
  const overlayRef = useRef();
  const bloomContainerRef = useRef();
  const characterRefs = useRef([]);
  const svgRef = useRef();

  // Image preloading logic remains the same...
  useEffect(() => {
    const imagesToLoad = CUTSCENE_SCRIPT.flatMap((scene) => {
      if (scene.type === 'showcase') {
        return [
          ...(scene.backgroundGradient ? [] : [getSpriteUrl(scene.background)]),
          ...(scene.foregroundCharacter ? [getSpriteUrl(
            scene.foregroundCharacter.character,
            scene.foregroundCharacter.variation
          )] : []),
          ...(scene.backgroundCharacter ? [getSpriteUrl(
            scene.backgroundCharacter.character,
            scene.backgroundCharacter.variation
          )] : []),
          ...(scene.svg ? [scene.svg] : [])
        ].filter(Boolean);
      } else if (scene.type === 'ensemble') {
        return [
          ...(scene.background ? [getSpriteUrl(scene.background)] : []),
          ...scene.characters.map((char) =>
            getSpriteUrl(char.character, char.variation)
          ),
        ];
      } else {
        throw new Error(`Invalid scene type: ${scene.type}`);
      }
    });

    Promise.all(
      imagesToLoad.map(
        (src) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = src;
          })
      )
    ).then(() => setImagesPreloaded(true));
  }, []);

  useEffect(() => {
    if (currentScene >= CUTSCENE_SCRIPT.length) {
      onComplete();
      return;
    }

    if (!imagesPreloaded || !containerRef.current) return;

    const scene = CUTSCENE_SCRIPT[currentScene];
    const containerWidth = containerRef.current.offsetWidth;

    gsap.set(bloomContainerRef.current, { filter: 'brightness(1) blur(0px)' });
    gsap.set(overlayRef.current, { opacity: 0 });

    if (bgImageRef.current) {
      gsap.set(bgImageRef.current, {
        x: 0, y: 0, scale: 1.0,
        transformOrigin: 'center center'
      });
    }

    if (scene.svg && svgRef.current) {
      gsap.fromTo(svgRef.current,
        { scale: 0.8, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 1,
          ease: 'power2.out'
        }
      );
    }

    if (scene.type === 'showcase') {
      // Background animation logic remains the same...
      if (scene.backgroundAnimation && !scene.backgroundGradient) {
        if (scene.backgroundAnimation.zoom) {
          gsap.to(bgImageRef.current, {
            scale: scene.backgroundAnimation.zoom,
            duration: scene.sceneDuration,
            ease: 'none',
          });
        } else if (scene.backgroundAnimation.startPosition && scene.backgroundAnimation.endPosition) {
          gsap.fromTo(bgImageRef.current,
            {
              x: scene.backgroundAnimation.startPosition.x,
              y: scene.backgroundAnimation.startPosition.y,
            },
            {
              x: scene.backgroundAnimation.endPosition.x,
              y: scene.backgroundAnimation.endPosition.y,
              duration: scene.sceneDuration,
              ease: 'none',
            }
          );
        }
      }

      if (scene.foregroundCharacter) {
        gsap.set(foregroundRef.current, {
          x: scene.foregroundCharacter.startPosition.x * containerWidth,
          y: scene.foregroundCharacter.startPosition.y,
          scale: scene.foregroundCharacter.scale,
          xPercent: -50,
        });

        const foregroundTl = gsap.timeline();
        foregroundTl
          .to(foregroundRef.current, {
            x: scene.foregroundCharacter.endPosition.x * containerWidth,
            y: scene.foregroundCharacter.endPosition.y,
            duration: scene.foregroundCharacter.initialAnimationDuration,
            ease: 'none',
          })
          .to(foregroundRef.current, {
            x: `+=${scene.foregroundCharacter.continuation.distance * containerWidth}`,
            duration: scene.foregroundCharacter.continuation.distance,
            ease: 'none',
          });
      }

      if (scene.backgroundCharacter) {
        gsap.set(backgroundRef.current, {
          x: scene.backgroundCharacter.startPosition.x * containerWidth,
          y: scene.backgroundCharacter.startPosition.y,
          scale: scene.backgroundCharacter.scale,
          xPercent: -50,
        });

        const backgroundTl = gsap.timeline();
        backgroundTl
          .to(backgroundRef.current, {
            x: scene.backgroundCharacter.endPosition.x * containerWidth,
            y: scene.backgroundCharacter.endPosition.y,
            duration: scene.backgroundCharacter.initialAnimationDuration,
            ease: 'none',
          })
          .to(backgroundRef.current, {
            x: `+=${scene.backgroundCharacter.continuation.distance * containerWidth}`,
            duration: scene.backgroundCharacter.continuation.duration,
            ease: 'none',
          });
      }
    } else if (scene.type === 'ensemble') {
      if (bgImageRef.current) {
        gsap.fromTo(
          bgImageRef.current,
          { x: scene.backgroundParallax.startX },
          {
            x: scene.backgroundParallax.endX,
            duration: scene.sceneDuration,
            ease: 'none',
          }
        );
      }

      scene.characters.forEach((char, index) => {
        const ref = characterRefs.current[index];
        if (!ref) return;

        const VIEW_WIDTH = 390;
        const VIEW_HEIGHT = 844;
        const CAMERA_Z = 500;
        const CAMERA_ANGLE = 70;

        const z = CAMERA_Z * (1 - char.zPosition);

        const angleRad = (CAMERA_ANGLE * Math.PI) / 180;
        const projectionFactor = CAMERA_Z / z;

        const yBase = VIEW_HEIGHT * 0.3;
        const verticalOffset = z * Math.tan(angleRad) * 0.4;
        const yPosition = yBase - verticalOffset;

        const baseScale = 1.8;
        const scaleReduction = 1 - (z / CAMERA_Z) * 0.5;
        const scale = baseScale * scaleReduction;

        const viewToWorldRatio = VIEW_WIDTH;
        const startX = char.startX * viewToWorldRatio;

        const baseMovement = scene.characterMovement.distance;
        const moveDistance = baseMovement * scaleReduction;

        gsap.set(ref, {
          x: startX,
          y: yPosition,
          scale: scale,
          xPercent: -50,
        });

        gsap.to(ref, {
          x: `+=${moveDistance}`,
          duration: scene.sceneDuration,
          ease: 'none',
        });
      });
    } else {
      throw new Error(`Invalid scene type: ${scene.type}`);
    }

    function handleSceneTransition() {
      setIsTransitioning(true);
      if (scene.transitionType === 'bloom') {
        const bloomElement = bloomContainerRef.current;
        gsap.to(bloomElement, {
          filter: 'brightness(2) blur(20px)',
          duration: scene.fadeDuration,
          onComplete: () => {
            setCurrentScene((prev) => prev + 1);
            setIsTransitioning(false);
          },
        });
      } else {
        const overlayElement = overlayRef.current;
        gsap.to(overlayElement, {
          opacity: scene.fadeOpacity,
          duration: scene.fadeDuration,
          onComplete: () => {
            setCurrentScene((prev) => prev + 1);
            setIsTransitioning(false);
          },
        });
      }
    }

    const transitionDelay = scene.sceneDuration - scene.fadeDuration;
    gsap.delayedCall(transitionDelay, handleSceneTransition);

    return () => {
      gsap.killTweensOf(foregroundRef.current);
      gsap.killTweensOf(backgroundRef.current);
      gsap.killTweensOf(bgImageRef.current);
      gsap.killTweensOf(overlayRef.current);
      gsap.killTweensOf(bloomContainerRef.current);
      gsap.killTweensOf(handleSceneTransition);
      if (svgRef.current) gsap.killTweensOf(svgRef.current);
      characterRefs.current.forEach((ref) => {
        if (ref) gsap.killTweensOf(ref);
      });
    };
  }, [currentScene, onComplete, imagesPreloaded]);

  if (currentScene >= CUTSCENE_SCRIPT.length || !imagesPreloaded) return null;

  const scene = CUTSCENE_SCRIPT[currentScene];

  return (
    <div ref={containerRef} className="w-screen h-screen relative overflow-hidden">
      <div
        ref={bloomContainerRef}
        className="w-full h-full relative"
        style={{
          filter: 'brightness(1) blur(0px)',
          transition: 'filter 0.3s ease-out',
        }}
      >
        <div className="absolute inset-0 z-10">
          {scene.backgroundGradient ? (
            <div
              className="w-full h-full"
              style={{
                background: `linear-gradient(to bottom, ${scene.backgroundGradient.startColor}, ${scene.backgroundGradient.endColor})`,
              }}
            />
          ) : (
            <img
              ref={bgImageRef}
              src={getSpriteUrl(scene.background)}
              className="absolute h-full w-auto origin-center"
              style={{
                minWidth: 'unset',
                maxWidth: 'none'
              }}
              alt="background"
            />
          )}
        </div>

        {scene.motionLines && (
          <MotionLines
            duration={scene.sceneDuration}
            rotation={scene.motionLines.rotation}
          />
        )}

        {scene.svg && (
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <img
              ref={svgRef}
              src={scene.svg}
              alt="Title"
              className="w-[350px] h-auto"
            />
          </div>
        )}

        {scene.type === 'showcase' && !scene.svg && (
          <>
            {scene.backgroundCharacter && (
              <div
                ref={backgroundRef}
                className="w-[416px] h-[624px] absolute bottom-0 left-1/2 z-20"
              >
                <img
                  src={getSpriteUrl(
                    scene.backgroundCharacter.character,
                    scene.backgroundCharacter.variation
                  )}
                  alt={scene.backgroundCharacter.character}
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            {scene.foregroundCharacter && (
              <div
                ref={foregroundRef}
                className="w-[416px] h-[624px] absolute bottom-0 left-1/2 z-30"
              >
                <img
                  src={getSpriteUrl(
                    scene.foregroundCharacter.character,
                    scene.foregroundCharacter.variation
                  )}
                  alt={scene.foregroundCharacter.character}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </>
        )}

        {scene.type === 'ensemble' && (
          scene.characters
            .sort((a, b) => a.zPosition - b.zPosition)
            .map((char, index) => (
              <div
                key={index}
                ref={(el) => (characterRefs.current[index] = el)}
                className="w-[416px] h-[624px] absolute bottom-0 left-1/2 z-20"
              >
                <img
                  src={getSpriteUrl(char.character, char.variation)}
                  alt={char.character}
                  className="w-full h-full object-contain"
                />
              </div>
            ))
        )}

        <div
          className="absolute inset-0 z-30 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, transparent 40%, rgba(0,0,0,0.4) 100%)',
          }}
        />
      </div>

      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black opacity-0 z-50"
      />
    </div>
  );
};

export default Cutscene;
